import { useState, useCallback } from "react";
import { RecognizedMedication, MedicationAIResponse, AIStatus, ValidationResponse } from "./types";
import { AIConfig } from "../../../types/landingPage";
import { makeBusinessDecision, BusinessDecision } from "./businessLogic";
import { track, getAnonymousId } from "../../../utils/rudderstack";
import { v4 as uuidv4 } from "uuid";
import { withScope, captureException } from "@sentry/react";

export const useMedicationAI = (aiConfig?: AIConfig) => {
  // Generate session ID for this AI interaction
  const [sessionId] = useState(() => uuidv4());
  const [status, setStatus] = useState<AIStatus>(AIStatus.IDLE);
  const [medications, setMedications] = useState<RecognizedMedication[]>([]);
  const [businessDecision, setBusinessDecision] = useState<BusinessDecision | null>(null);
  const [validation, setValidation] = useState<ValidationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<MedicationAIResponse | null>(null);

  const analyzeMedications = useCallback(
    async (text: string) => {
      if (!text.trim()) {
        setMedications([]);
        return;
      }

      setStatus(AIStatus.PROCESSING);
      setIsLoading(true);
      setError(null);
      
      // Track medication analysis started
      track("lp.ai_medication_analysis_started", {
        sessionId,
        textLength: text.length,
        variant: "ai"
      });

      try {
        // Agent 1: Medication Recognition
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        const startTime = Date.now();

        const response = await fetch("/.netlify/functions/medication-ai", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text,
            studyContext: aiConfig?.studyContext,
            medicationCategories: aiConfig?.medicationCategories,
            agentType: "recognition",
            // Add tracking data
            sessionId,
            userId: getAnonymousId() || undefined,
            landingPageId: window.location.pathname,
            variant: "ai",
            experimentName: aiConfig?.experimentName
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Failed to analyze medications: ${response.statusText}`);
        }

        const data: MedicationAIResponse = await response.json();
        
        // Map medications to grid format values
        const mappedMedications = data.medications.map((med) => ({
          ...med,
          gridValue: generateGridValue(med, aiConfig),
        }));

        setMedications(mappedMedications);
        setResponse(data);
        setStatus(AIStatus.CONFIRMING);
        
        // Track successful recognition
        track("lp.ai_medication_recognition_completed", {
          sessionId,
          duration: Date.now() - startTime,
          medicationCount: data.medications.length,
          needsClarificationCount: data.needsClarification?.length || 0,
          variant: "ai"
        });
        
        // Return the response for seamless flow handling
        return data;
      } catch (err) {
        let errorMessage = "Failed to analyze medications";
        if (err instanceof Error) {
          if (err.name === 'AbortError') {
            errorMessage = "Request timed out. Please try again.";
          } else {
            errorMessage = err.message;
          }
        }
        
        withScope((scope) => {
          scope.setExtra('sessionId', sessionId);
          scope.setExtra('textLength', text.length);
          scope.setTag('component', 'useMedicationAI');
          scope.setTag('error', 'analyze_medications');
          captureException(err);
        });
        
        console.error("Error analyzing medications:", err);
        
        // Track error
        track("lp.ai_medication_recognition_error", {
          sessionId,
          error: errorMessage,
          variant: "ai"
        });
        
        setError(errorMessage);
        setStatus(AIStatus.ERROR);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [aiConfig, sessionId]
  );

  const validateDecision = useCallback(
    async (medicationsToValidate: RecognizedMedication[], decision: BusinessDecision, originalText?: string) => {
      setStatus(AIStatus.VALIDATING);
      setIsLoading(true);
      setError(null);
      
      // Track validation started
      track("lp.ai_medication_validation_started", {
        sessionId,
        medicationCount: medicationsToValidate.length,
        businessDecision: decision.decision,
        variant: "ai"
      });

      try {
        // Agent 2: Validation
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        const startTime = Date.now();

        const response = await fetch("/.netlify/functions/medication-ai", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text: originalText, // Pass the original user text for validation
            medications: medicationsToValidate,
            businessDecision: decision,
            studyContext: aiConfig?.studyContext,
            medicationCategories: aiConfig?.medicationCategories,
            agentType: "validation",
            // Add tracking data
            sessionId,
            userId: getAnonymousId() || undefined,
            landingPageId: window.location.pathname,
            variant: "ai",
            experimentName: aiConfig?.experimentName
          }),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          // If validation fails, we can still proceed with business decision
          console.warn("Validation failed, proceeding with business decision only");
          setValidation(null);
          setStatus(AIStatus.COMPLETE);
          return decision;
        }

        const validationResult: ValidationResponse = await response.json();
        setValidation(validationResult);
        setStatus(AIStatus.COMPLETE);
        
        // Track successful validation
        track("lp.ai_medication_validation_completed", {
          sessionId,
          duration: Date.now() - startTime,
          validation: validationResult.validation,
          suggestedAction: validationResult.suggestedAction,
          confidence: validationResult.confidence,
          variant: "ai"
        });
        
        return decision;
      } catch (err) {
        withScope((scope) => {
          scope.setExtra('sessionId', sessionId);
          scope.setExtra('decision', decision.decision);
          scope.setTag('component', 'useMedicationAI');
          scope.setTag('error', 'validate_decision');
          captureException(err);
        });
        
        console.error("Error validating decision:", err);
        
        // Track validation error
        track("lp.ai_medication_validation_error", {
          sessionId,
          error: err instanceof Error ? err.message : "Unknown error",
          variant: "ai"
        });
        
        // Don't fail the entire process if validation fails
        console.warn("Validation failed, proceeding with business decision only");
        setValidation(null);
        setStatus(AIStatus.COMPLETE);
        return decision;
      } finally {
        setIsLoading(false);
      }
    },
    [aiConfig, sessionId]
  );

  const updateMedication = useCallback(
    (index: number, updates: Partial<RecognizedMedication>) => {
      setMedications((prev) =>
        prev.map((med, i) =>
          i === index
            ? {
                ...med,
                ...updates,
                gridValue: generateGridValue({ ...med, ...updates }, aiConfig),
              }
            : med
        )
      );
    },
    [aiConfig]
  );

  const removeMedication = useCallback((index: number) => {
    setMedications((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addMedication = useCallback(
    (medication: Partial<RecognizedMedication>) => {
      const newMed: RecognizedMedication = {
        name: medication.name || "",
        status: medication.status || "current",
        confidence: medication.confidence || 0.5,
        matchedText: medication.matchedText || medication.name || "",
        ...medication,
      };
      newMed.gridValue = generateGridValue(newMed, aiConfig);
      setMedications((prev) => [...prev, newMed]);
    },
    [aiConfig]
  );

  const confirmMedications = useCallback(async (medicationsToConfirm?: RecognizedMedication[], originalText?: string) => {
    // Use passed medications or fall back to state
    const meds = medicationsToConfirm || medications;
    
    // Track user confirming medications
    track("lp.ai_medication_confirmed", {
      sessionId,
      medicationCount: meds.length,
      variant: "ai",
      flowType: "seamless"
    });
    
    // Step 1: Make business decision based on config rules
    const decision = makeBusinessDecision(meds, aiConfig);
    setBusinessDecision(decision);
    
    // Track business decision
    track("lp.ai_medication_business_decision", {
      sessionId,
      decision: decision.decision,
      flaggedCount: decision.flaggedMedications.length,
      variant: "ai"
    });
    
    // Step 2: Validate the decision with AI
    // Use explicitly passed original text, or fall back to response state
    const textForValidation = originalText || response?.rawText;
    await validateDecision(meds, decision, textForValidation);
    return decision;
  }, [medications, aiConfig, validateDecision, sessionId, response]);

  const reset = useCallback(() => {
    setStatus(AIStatus.IDLE);
    setMedications([]);
    setBusinessDecision(null);
    setValidation(null);
    setError(null);
    setResponse(null);
  }, []);

  const resetToConfirming = useCallback(() => {
    setStatus(AIStatus.CONFIRMING);
    setBusinessDecision(null);
    setValidation(null);
    setError(null);
  }, []);

  return {
    status,
    medications,
    businessDecision,
    validation,
    error,
    isLoading,
    response,
    analyzeMedications,
    validateDecision,
    updateMedication,
    removeMedication,
    addMedication,
    confirmMedications,
    reset,
    resetToConfirming,
    sessionId,
  };
};

// Helper function to generate grid-compatible values
export function generateGridValue(
  medication: RecognizedMedication,
  aiConfig?: AIConfig
): string {
  // Always generate a value for every medication
  // For study-specific medications, use the proper mapping
  const category = aiConfig?.medicationCategories.find((cat) =>
    cat.medications.some((med) =>
      med.toLowerCase().includes(medication.name.toLowerCase()) ||
      (medication.genericName && med.toLowerCase().includes(medication.genericName.toLowerCase()))
    )
  );

  if (category) {
    // Study-specific medication - use proper mapping
    const medKey = medication.genericName?.toLowerCase() || medication.name.toLowerCase();
    return `${medKey}_${medication.status}`;
  } else {
    // Non-study medication - still create a value so it's preserved
    const medKey = (medication.genericName || medication.name).toLowerCase().replace(/\s+/g, '_');
    return `other_${medKey}_${medication.status}`;
  }
}